import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, resolveDynamicComponent as _resolveDynamicComponent } from "vue"

import { computed, ref } from 'vue'
import { useStore } from 'vuex'
import { useGeneralStore } from '@/pinia'
import { MAPVIEW_PREFIX } from '@/store/module-prefixes'
import { RESET_CURRENT_TOOL, SET_DRAW_POINT_FEATURE } from '@/store/mutation-types'
import {
  LayerBuilding,
  LayerBuildingMarkerCluster,
  LayerComplexMarker,
  LayerComplexMarkerCluster,
  LayerDrawPoint,
  LayerDrawingOutdoor,
  LayerDrawingOutdoorTiles,
  LayerLandZone,
  LayerLineOutdoor,
  LayerMarkerBuilding,
  LayerMarkerTickets,
  LayerOrthophotoOutdoor,
  LayerOrthophotoOutdoorTiles,
  LayerPoiOutdoorMarker,
  LayerRealEstate,
  LayerSatellite,
  LayerVectorTile,
  LayerWms
} from './index'
import { getRealmId } from '@/utils/realmId'
import { storeToRefs } from 'pinia'
import { useLayers } from '@/pinia/layers'

// Props definition

export default /*@__PURE__*/_defineComponent({
  __name: 'MapViewLayers',
  props: {
    complexSource: {},
    realEstateSource: {},
    buildingSource: {}
  },
  setup(__props: any) {



const store = useStore()
const generalStore = useGeneralStore()
const { setCurrentOverlayModalPinia, setRightClickCoordinate, setShowOverlayModalPinia } =
  generalStore

const layerIdBuilding = ref('layer-building')
const layerIdBuildingMarkerCluster = ref('layer-building-marker-cluster')
const layerIdComplexMarker = ref('layer-complex-marker')
const layerIdDrawingOutdoor = ref('layer-drawing-outdoor')
const layerIdDrawingOutdoorTiles = ref('layer-drawing-outdoor-tiles')
const layerIdMarkerBuilding = ref('layer-marker-building')
const layerIdOrthophotoOutdoor = ref('layer-orthophoto-outdoor')
const layerIdOrthophotoOutdoorTiles = ref('layer-orthophoto-outdoor-tiles')
const layerIdPoiOutdoorMarker = ref('layer-poi-outdoor-marker')
const layerIdPoiOutdoorMarkerSelected = ref('layer-poi-outdoor-marker-selected')
const layerIdRealEstate = ref('layer-real-estate')
const layerIdVectorTile = ref('layer-vector-tile')
const layerIdWms = ref('layer-wms')
const poiLayerCreated = ref(false)

const {
  filteredDrawings,
  filteredLineLayers,
  filteredOrthophotos,
  filteredWmsLayers,
  showFeatureErrands
} = storeToRefs(generalStore)

const { orderedLayers } = storeToRefs(useLayers())

const hasDrawingAccess = computed(() => store.getters.hasDrawingAccess)
const hasLineReaderAccess = computed(() => store.getters.hasLineReaderAccess)
const hasOrthophotoAccess = computed(() => store.getters.hasOrthophotoAccess)
const hasPoiReaderAccess = computed(() => store.getters.hasPoiReaderAccess)
const hasZoneAccess = computed(() => store.getters.hasZoneAccess)
const isDrawPointActive = computed(() => store.getters.isDrawPointActive)

const mountedMapLayerIds = computed(() => store.state.mountedMapLayerIds)
const selectedPoi = computed(() => store.state.selectedPoi)

const mapVectorTileState = computed(() => store.state[MAPVIEW_PREFIX].mapVectorTileState)
const filteredMapLayers = computed(() => store.state[MAPVIEW_PREFIX].filteredLayers)
const outdoorPoisForSelectedRealEstate = computed(
  () => store.state[MAPVIEW_PREFIX].outdoorPoisForSelectedRealEstate
)

const fullPoi = computed(() => {
  if (selectedPoi.value) {
    return outdoorPoisForSelectedRealEstate.value.find(
      (poi: any) => poi.properties.guid === selectedPoi.value.guid
    )
  }
  return null
})

const isPoiLayerCreated = computed(() => {
  if (poiLayerCreated.value) {
    if (
      mountedMapLayerIds.value.find((id: string) => id === layerIdPoiOutdoorMarkerSelected.value)
    ) {
      return layerIdPoiOutdoorMarkerSelected.value
    }
    return layerIdPoiOutdoorMarker.value
  }
  return layerIdBuilding.value
})

const realmId = computed(() => getRealmId())

const shouldShowLandZones = computed(() => {
  return filteredMapLayers.value.some((layer: any) =>
    layer.types.some((type: any) => type.visible === true)
  )
})

const shouldShowOutdoorLines = computed(() => {
  return filteredLineLayers.value.some((layer: any) =>
    layer.types.some((type: any) => type.visible === true)
  )
})

const layerAboveBuilding = computed(() => {
  return mountedMapLayerIds.value.find(
    (id: string) => id === layerIdMarkerBuilding.value || id === layerIdComplexMarker.value
  )
})

const buildingLayerCreated = computed(() => {
  return mountedMapLayerIds.value.includes(layerIdBuilding.value)
})

const resetTool = () => {
  store.commit(RESET_CURRENT_TOOL)
}

const setDrawPointFeature = (point: any) => {
  store.commit(SET_DRAW_POINT_FEATURE, { point })
}

const handleDrawPoint = (point: any) => {
  setDrawPointFeature(point)
  resetTool()
}

const handleDrawPointMobile = (point: any, editMode: boolean) => {
  if (!point) return
  const rightClickCoordinate = point.geometry.coordinates
  if (!editMode) setRightClickCoordinate(rightClickCoordinate)
  setCurrentOverlayModalPinia('createAssets')
  setShowOverlayModalPinia(true)
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(_unref(LayerSatellite)),
    (_unref(filteredOrthophotos).length && hasOrthophotoAccess.value)
      ? (_openBlock(), _createBlock(_unref(LayerOrthophotoOutdoor), {
          key: 0,
          before: layerIdRealEstate.value,
          layerId: layerIdOrthophotoOutdoor.value
        }, null, 8, ["before", "layerId"]))
      : _createCommentVNode("", true),
    (_unref(filteredOrthophotos).length && hasOrthophotoAccess.value)
      ? (_openBlock(), _createBlock(_unref(LayerOrthophotoOutdoorTiles), {
          key: 1,
          before: layerIdRealEstate.value,
          layerId: layerIdOrthophotoOutdoorTiles.value
        }, null, 8, ["before", "layerId"]))
      : _createCommentVNode("", true),
    (_unref(filteredDrawings).length && hasDrawingAccess.value)
      ? (_openBlock(), _createBlock(_unref(LayerDrawingOutdoor), {
          key: 2,
          before: layerIdRealEstate.value,
          layerId: layerIdDrawingOutdoor.value
        }, null, 8, ["before", "layerId"]))
      : _createCommentVNode("", true),
    (_unref(filteredDrawings).length && hasDrawingAccess.value)
      ? (_openBlock(), _createBlock(_unref(LayerDrawingOutdoorTiles), {
          key: 3,
          before: layerIdRealEstate.value,
          layerId: layerIdDrawingOutdoorTiles.value
        }, null, 8, ["before", "layerId"]))
      : _createCommentVNode("", true),
    _createVNode(_unref(LayerRealEstate), {
      source: _ctx.realEstateSource,
      layerIdRealEstate: layerIdRealEstate.value
    }, null, 8, ["source", "layerIdRealEstate"]),
    (realmId.value === 'akademiskahus' && mapVectorTileState.value !== 'hidden')
      ? (_openBlock(), _createBlock(_unref(LayerVectorTile), {
          key: 4,
          before: layerIdBuilding.value,
          layerId: layerIdVectorTile.value
        }, null, 8, ["before", "layerId"]))
      : _createCommentVNode("", true),
    (_unref(filteredWmsLayers).length)
      ? (_openBlock(), _createBlock(_unref(LayerWms), {
          key: 5,
          before: layerIdRealEstate.value,
          layerId: layerIdWms.value
        }, null, 8, ["before", "layerId"]))
      : _createCommentVNode("", true),
    (buildingLayerCreated.value && shouldShowLandZones.value && hasZoneAccess.value)
      ? (_openBlock(), _createBlock(_unref(LayerLandZone), {
          key: 6,
          before: isPoiLayerCreated.value
        }, null, 8, ["before"]))
      : _createCommentVNode("", true),
    (buildingLayerCreated.value && shouldShowOutdoorLines.value && hasLineReaderAccess.value)
      ? (_openBlock(), _createBlock(_unref(LayerLineOutdoor), {
          key: 7,
          before: isPoiLayerCreated.value
        }, null, 8, ["before"]))
      : _createCommentVNode("", true),
    (hasPoiReaderAccess.value && buildingLayerCreated.value && !isDrawPointActive.value)
      ? (_openBlock(), _createBlock(_unref(LayerPoiOutdoorMarker), {
          key: 8,
          before: layerIdBuilding.value,
          layerId: layerIdPoiOutdoorMarker.value,
          layerIdSelected: layerIdPoiOutdoorMarkerSelected.value,
          onPoiLayerCreated: _cache[0] || (_cache[0] = ($event: any) => (poiLayerCreated.value = true)),
          onPoiLayerRemoved: _cache[1] || (_cache[1] = ($event: any) => (poiLayerCreated.value = false))
        }, null, 8, ["before", "layerId", "layerIdSelected"]))
      : _createCommentVNode("", true),
    (isDrawPointActive.value)
      ? (_openBlock(), _createBlock(_unref(LayerDrawPoint), {
          key: 9,
          pointSource: fullPoi.value,
          onSubmitPoint: handleDrawPoint,
          onSubmitPointMobile: handleDrawPointMobile
        }, null, 8, ["pointSource"]))
      : _createCommentVNode("", true),
    (_ctx.buildingSource && layerAboveBuilding.value)
      ? (_openBlock(), _createBlock(_unref(LayerBuilding), {
          key: 10,
          before: layerAboveBuilding.value,
          layerId: layerIdBuilding.value,
          source: _ctx.buildingSource
        }, null, 8, ["before", "layerId", "source"]))
      : _createCommentVNode("", true),
    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(orderedLayers), (layer) => {
      return (_openBlock(), _createElementBlock(_Fragment, {
        key: layer.order
      }, [
        (layer.visible)
          ? (_openBlock(), _createBlock(_resolveDynamicComponent(layer.component), {
              key: 0,
              opacity: layer.opacity,
              before: layer.before
            }, null, 8, ["opacity", "before"]))
          : _createCommentVNode("", true)
      ], 64))
    }), 128)),
    (_ctx.buildingSource)
      ? (_openBlock(), _createBlock(_unref(LayerMarkerBuilding), {
          key: 11,
          layerIdMarker: layerIdMarkerBuilding.value,
          buildingSource: _ctx.buildingSource,
          complexSource: _ctx.complexSource
        }, null, 8, ["layerIdMarker", "buildingSource", "complexSource"]))
      : _createCommentVNode("", true),
    (_ctx.buildingSource)
      ? (_openBlock(), _createBlock(_unref(LayerBuildingMarkerCluster), {
          key: 12,
          layerId: layerIdBuildingMarkerCluster.value,
          buildingSource: _ctx.buildingSource,
          complexSource: _ctx.complexSource
        }, null, 8, ["layerId", "buildingSource", "complexSource"]))
      : _createCommentVNode("", true),
    (_ctx.complexSource)
      ? (_openBlock(), _createBlock(_unref(LayerComplexMarker), {
          key: 13,
          layerId: layerIdComplexMarker.value,
          source: _ctx.complexSource,
          buildingSource: _ctx.buildingSource
        }, null, 8, ["layerId", "source", "buildingSource"]))
      : _createCommentVNode("", true),
    (_ctx.complexSource)
      ? (_openBlock(), _createBlock(_unref(LayerComplexMarkerCluster), {
          key: 14,
          source: _ctx.complexSource
        }, null, 8, ["source"]))
      : _createCommentVNode("", true),
    (_unref(showFeatureErrands) && !isDrawPointActive.value)
      ? (_openBlock(), _createBlock(_unref(LayerMarkerTickets), { key: 15 }))
      : _createCommentVNode("", true)
  ], 64))
}
}

})